import QRCode from 'qrcode';
import { supabase } from './supabaseClient';
import {
  EinweisungOnlineTeilnahme,
  EinweisungQrQuelle,
  SigeEinweisung,
} from '../components/sige-plan/types';
import { describeError } from './errorMessage';
import { einweisungLinkUrl, linkIstAktiv } from '../components/sige-einweisung/einweisungLogic';

/**
 * Datenbankzugriff der SiGe-Plan-Einweisung (Tabelle `sige_einweisungen`,
 * supabase/sige_einweisungen.sql, MIGRATIONS.md Nr. 35) — die einzige Stelle,
 * die mit ihr redet.
 *
 * Die REGELN liegen daneben in sigeEinweisung.ts und sind ohne Datenbank
 * pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist.
 *
 * ═══ ZWEI FALLEN, die dieses Modul bewusst zumauert ═══════════════════════
 *
 * (a) DIE LAUFENDE NUMMER WIRD NIE VOR EINEM `await` FESTGELEGT.
 *     Im Repo ist genau das schon einmal passiert (laufende Nummer der
 *     Begehungseintraege): die Nummer wurde beim Oeffnen der Maske ermittelt,
 *     der Anwender fuellte in Ruhe das Formular, und der Insert schrieb sie
 *     Minuten spaeter — auf einen Platz, den inzwischen ein anderer Eintrag
 *     hatte. Der mobile Assistent ueberschrieb so den vorigen Eintrag.
 *     Hier wird die Nummer INNERHALB der Insert-Schleife geholt, unmittelbar
 *     vor dem Schreiben, und der harte Riegel bleibt der Unique-Index
 *     (project_id, nummer): bei `23505` wird neu gelesen und erneut versucht.
 *
 * (b) `markiereGebucht` SCHREIBT NUR, WENN `gebucht_am` NOCH NULL IST.
 *     Die Bedingung steht in der WHERE-Klausel des UPDATE (`.is('gebucht_am',
 *     null)`), nicht in einem vorgelagerten SELECT — ein Lesen-dann-Schreiben
 *     haette zwischen beiden Schritten genau das Fenster, das eine
 *     Doppelbuchung braucht. Abgerechnet wird GENAU EINMAL je Einweisung; ein
 *     zweiter Export darf die LV-Position nicht ein zweites Mal belasten.
 *
 * ═══ EINGEFROREN heisst eingefroren ══════════════════════════════════════
 * Eine abgeschlossene Einweisung ist schreibgeschuetzt, und der Riegel sitzt
 * HIER und nicht nur in der Oberflaeche: unter dem Blatt stehen Unterschriften,
 * und ein Textbaustein, der sich nachtraeglich aendert, macht sie wertlos. Auch
 * dieser Riegel ist eine WHERE-Bedingung (`.eq('status', 'entwurf')`), kein
 * vorgelagerter Lesevorgang.
 *
 * Ausgenommen sind die reinen BUCHUNGSFELDER: gebucht wird naturgemaess NACH
 * dem Abschliessen. Sie haben deshalb ihren eigenen Weg (markiereGebucht) mit
 * ihrem eigenen Riegel.
 */

const TABELLE = 'sige_einweisungen';

/** Privater Bucket fuer in Papierform durchgefuehrte Einweisungen (Scan/Foto/PDF). */
const BUCKET = 'sige-einweisungen';

/** Gueltigkeit einer signierten Download-URL. Eine Stunde reicht zum Ansehen. */
const SIGNED_URL_SEKUNDEN = 3600;

/** Wie oft der Insert bei Nummernkollision erneut versucht wird. */
const MAX_NUMMER_VERSUCHE = 4;

/**
 * Fehlercode eines Supabase-Fehlers, soweit vorhanden.
 *
 * Supabase wirft schlichte Objekte, keine Error-Instanzen (siehe
 * errorMessage.ts) — `e.code` ist nur ueber einen Cast erreichbar.
 */
const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** 23505 = unique_violation, hier: (project_id, nummer) ist schon vergeben. */
const istNummernKollision = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Die Tabelle gibt es noch nicht (Migration Nr. 35 offen).
 *
 * PGRST205 meldet PostgREST, 42P01 ist der Postgres-Code fuer
 * "undefined_table" — welcher ankommt, haengt daran, ob PostgREST das Schema
 * schon kennt. Muster wie invoiceCostCenters.ts.
 */
const istFehlendeTabelle = (e: unknown): boolean => {
  const code = errorCode(e);
  return code === 'PGRST205' || code === '42P01';
};

/**
 * Spalten, die NIE aus einem Update-Objekt uebernommen werden.
 *
 * `id`, `project_id` und `nummer` sind die Identitaet der Zeile; die
 * gebucht_*-Felder gehoeren markiereGebucht (siehe Falle (b) oben). Ein
 * Aufrufer, der arglos die ganze Einweisung zurueckschreibt, wuerde sonst eine
 * Buchung ueberschreiben oder eine Nummer verschieben.
 *
 * `link_token`/`link_aktiv_bis` gehoeren der RPC `sige_einweisung_link_setzen`
 * (setzeEinweisungLink): dort sitzt die Regel „nur abgeschlossen", und ein
 * Autosave des Editors darf einen aktiven Link weder loeschen noch setzen.
 */
const GESPERRTE_SPALTEN = [
  'id',
  'project_id',
  'nummer',
  'gebucht_am',
  'gebuchte_position_id',
  'gebuchte_menge',
  'link_token',
  'link_aktiv_bis',
  'created_at',
] as const;

const ohneGesperrteSpalten = (updates: Partial<SigeEinweisung>): Record<string, unknown> => {
  const rein: Record<string, unknown> = { ...updates };
  for (const spalte of GESPERRTE_SPALTEN) delete rein[spalte];
  return rein;
};

/** Vorgaben fuer Felder, die die Datenbank NULL-bar haelt, der Client aber nie NULL sehen soll. */
const mapZeile = (row: Record<string, unknown>): SigeEinweisung => ({
  ...(row as unknown as SigeEinweisung),
  punkte: Array.isArray(row.punkte) ? (row.punkte as SigeEinweisung['punkte']) : [],
  teilnehmer: Array.isArray(row.teilnehmer) ? (row.teilnehmer as SigeEinweisung['teilnehmer']) : [],
  leerzeilen: typeof row.leerzeilen === 'number' ? row.leerzeilen : 5,
  // Vor Migration Nr. 97 fehlen die Spalten ganz — dann gibt es eben keinen Link.
  link_token: typeof row.link_token === 'string' && row.link_token ? row.link_token : null,
  link_aktiv_bis: typeof row.link_aktiv_bis === 'string' && row.link_aktiv_bis ? row.link_aktiv_bis : null,
});

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Alle Einweisungen eines Projekts, neueste Nummer zuerst.
 *
 * Wirft NICHT bei fehlender Tabelle: solange Migration Nr. 35 nicht gefahren
 * ist, gibt es eben keine Einweisungen. Eine offene Migration darf die
 * Projektansicht nicht aufreissen — dieselbe Zusicherung wie bei
 * fetchInvoiceCostCenters. Jeder andere Fehler (Netz, kaputte Antwort)
 * schlaegt durch.
 *
 * ACHTUNG, was hier NICHT geht: fehlende Berechtigung von „nichts vorhanden"
 * zu unterscheiden. RLS antwortet beim SELECT nicht mit einem Fehler, sondern
 * mit einer LEEREN MENGE. Wer das Projekt nicht ueber `can_access_project`
 * haelt (Migration Nr. 35), sieht deshalb „Noch keine Einweisung abgelegt" —
 * und merkt es erst, wenn das Anlegen mit 42501 scheitert. Wer das aufloesen
 * will, muss den Projektzugriff getrennt erfragen; ein `select` auf diese
 * Tabelle kann die Frage grundsaetzlich nicht beantworten.
 */
export const fetchEinweisungen = async (projectId: string): Promise<SigeEinweisung[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(TABELLE)
      .select('*')
      .eq('project_id', id)
      .order('nummer', { ascending: false });
    if (error) throw error;
    return (data ?? []).map((r) => mapZeile(r as Record<string, unknown>));
  } catch (e) {
    if (istFehlendeTabelle(e)) {
      console.warn('sige_einweisungen fehlt — Migration Nr. 35 noch nicht gefahren.');
      return [];
    }
    throw new Error(describeError(e));
  }
};

/** Eine einzelne Einweisung. `null`, wenn es sie nicht gibt (oder RLS sie verbirgt). */
export const fetchEinweisung = async (id: string): Promise<SigeEinweisung | null> => {
  const { data, error } = await supabase.from(TABELLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(describeError(error));
  return data ? mapZeile(data as Record<string, unknown>) : null;
};

// ─── Anlegen ────────────────────────────────────────────────────────────────

/**
 * Die HOECHSTE bisher vergebene Nummer + 1, frisch aus der Datenbank.
 *
 * Bewusst eine eigene, winzige Abfrage statt eines Griffs in eine schon
 * geladene Liste: die Liste im Speicher ist Sekunden bis Minuten alt, und
 * genau diese Zeitspanne ist Falle (a). Lueckenfuellung findet nicht statt
 * (Begruendung bei naechsteEinweisungsNummer in sigeEinweisung.ts).
 */
const naechsteNummerAusDb = async (projectId: string): Promise<number> => {
  const { data, error } = await supabase
    .from(TABELLE)
    .select('nummer')
    .eq('project_id', projectId)
    .order('nummer', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  const hoechste = Number((data as { nummer?: unknown } | null)?.nummer ?? 0);
  return Number.isFinite(hoechste) && hoechste > 0 ? hoechste + 1 : 1;
};

/** Der Firmen-Anzeigename des angemeldeten Profils — best effort, nie blockierend. */
const eigenerFirmenname = async (): Promise<string | null> => {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return null;
    const { data } = await supabase
      .from('profiles')
      .select('company_name')
      .eq('id', userId)
      .maybeSingle();
    const name = (data as { company_name?: string } | null)?.company_name;
    return name?.trim() ? name.trim() : null;
  } catch {
    // Der Firmenname ist Anzeige, kein Riegel. Fehlt er, wird die Einweisung
    // trotzdem angelegt — sonst haengt das Anlegen an einer Nebensache.
    return null;
  }
};

/**
 * Die Felder, die der Aufrufer beim Anlegen setzen darf. Alles Uebrige — id,
 * nummer, status, Buchung, Zeitstempel — vergibt dieses Modul bzw. die
 * Datenbank.
 */
export type NeueEinweisung = Partial<
  Omit<
    SigeEinweisung,
    | 'id'
    | 'nummer'
    | 'status'
    | 'gebucht_am'
    | 'gebuchte_position_id'
    | 'gebuchte_menge'
    | 'created_at'
    | 'updated_at'
  >
> & { project_id: string };

/**
 * Legt eine Einweisung als ENTWURF an.
 *
 * Die laufende Nummer wird in der Schleife unmittelbar vor dem Insert geholt
 * (Falle (a)). Faellt ein paralleler Anleger dazwischen, antwortet der
 * Unique-Index mit `23505`; dann wird neu gelesen und erneut versucht. Alle
 * anderen Fehler schlagen sofort durch — ein Wiederholen wuerde sie nur
 * verschleiern.
 */
export const createEinweisung = async (input: NeueEinweisung): Promise<SigeEinweisung> => {
  const projectId = (input.project_id || '').trim();
  if (!projectId) throw new Error('Ohne Projekt lässt sich keine Einweisung anlegen.');

  // Vor der Schleife, weil es nichts mit der Nummer zu tun hat und sonst bei
  // jedem Wiederholungsversuch erneut abgefragt wuerde.
  const companyName = input.company_name ?? (await eigenerFirmenname());

  for (let versuch = 1; ; versuch++) {
    const nummer = await naechsteNummerAusDb(projectId);

    const zeile = {
      ...input,
      project_id: projectId,
      nummer,
      status: 'entwurf' as const,
      quelle: input.quelle ?? 'digital',
      punkte: input.punkte ?? [],
      teilnehmer: input.teilnehmer ?? [],
      leerzeilen: input.leerzeilen ?? 5,
      datum: input.datum ?? new Date().toISOString().slice(0, 10),
      company_name: companyName,
    };

    const { data, error } = await supabase.from(TABELLE).insert(zeile).select('*').single();
    if (!error) return mapZeile(data as Record<string, unknown>);

    if (!istNummernKollision(error) || versuch >= MAX_NUMMER_VERSUCHE) {
      throw new Error(describeError(error));
    }
    // Naechste Runde: Nummer wird oben frisch geholt.
  }
};

// ─── Ändern ─────────────────────────────────────────────────────────────────

/**
 * Meldung, wenn ein Schreibversuch keine Zeile getroffen hat.
 *
 * Zwei Ursachen sind zu unterscheiden, und der Unterschied ist fuer den
 * Anwender wichtig: „schon abgeschlossen" ist eine fachliche Aussage, „nicht
 * gefunden" ein technisches Problem. Deshalb erst hier — NACH dem
 * fehlgeschlagenen Schreiben — nachgesehen, was los ist.
 */
const erklaereLeerenTreffer = async (id: string, vorgang: string): Promise<never> => {
  const vorhanden = await fetchEinweisung(id);
  if (!vorhanden) {
    throw new Error(`Diese Einweisung wurde nicht gefunden (${vorgang}).`);
  }
  if (vorhanden.status === 'abgeschlossen') {
    throw new Error(
      'Diese Einweisung ist abgeschlossen und damit schreibgeschützt. ' +
        'Für Änderungen bitte eine neue Einweisung anlegen.',
    );
  }
  throw new Error(`Die Einweisung konnte nicht gespeichert werden (${vorgang}).`);
};

/**
 * Ändert eine Einweisung im Entwurf.
 *
 * `.eq('status', 'entwurf')` ist der Guard und sitzt im UPDATE selbst: ein
 * vorgelagertes SELECT liesse zwischen Pruefung und Schreiben genau das
 * Fenster offen, in dem ein zweiter Browser abschliesst.
 */
export const updateEinweisung = async (
  id: string,
  updates: Partial<SigeEinweisung>,
): Promise<SigeEinweisung> => {
  const zeile = ohneGesperrteSpalten(updates);
  // `status` bleibt zugelassen — aber nur der Weg zurueck in den Entwurf waere
  // damit moeglich, und der ist es nicht: die WHERE-Bedingung greift ohnehin
  // nur auf Entwuerfe. Abschliessen laeuft ueber abschliessenEinweisung.
  delete zeile.status;
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(TABELLE)
    .update(zeile)
    .eq('id', id)
    .eq('status', 'entwurf')
    .select('*');
  if (error) throw new Error(describeError(error));
  if (!data || data.length === 0) return erklaereLeerenTreffer(id, 'Speichern');
  return mapZeile(data[0] as Record<string, unknown>);
};

/**
 * Friert die Einweisung ein: Status `abgeschlossen`, danach schreibgeschützt.
 *
 * Der Planbezug (`plan_nr`, `plan_stand`, `sige_projekt_id`) und die Punkte
 * liegen zu diesem Zeitpunkt bereits als eigene Kopie am Datensatz — genau
 * dafuer sind sie dort. Ein spaeterer Planwechsel kann das unterschriebene
 * Blatt deshalb nicht mehr veraendern.
 *
 * IDEMPOTENT: eine bereits abgeschlossene Einweisung wird unveraendert
 * zurueckgegeben statt mit einem Fehler quittiert. Zweimal auf „Abschließen"
 * zu klicken ist keine Fehlbedienung, die eine Meldung verdient — das Ergebnis
 * ist beide Male dasselbe.
 */
export const abschliessenEinweisung = async (id: string): Promise<SigeEinweisung> => {
  const jetzt = new Date().toISOString();
  const { data, error } = await supabase
    .from(TABELLE)
    .update({ status: 'abgeschlossen', updated_at: jetzt })
    .eq('id', id)
    .eq('status', 'entwurf')
    .select('*');
  if (error) throw new Error(describeError(error));
  if (data && data.length > 0) return mapZeile(data[0] as Record<string, unknown>);

  const vorhanden = await fetchEinweisung(id);
  if (!vorhanden) throw new Error('Diese Einweisung wurde nicht gefunden (Abschließen).');
  return vorhanden;
};

// ─── Papierform: Datei ──────────────────────────────────────────────────────

/** Dateiendung aus dem Namen, klein, ohne Punkt. Ohne erkennbare Endung: 'pdf'. */
const endungAus = (fileName: string): string => {
  const treffer = /\.([a-zA-Z0-9]{1,8})$/.exec((fileName || '').trim());
  return treffer ? treffer[1].toLowerCase() : 'pdf';
};

/**
 * Laedt den Scan bzw. das Foto einer auf Papier durchgefuehrten Einweisung in
 * den Bucket `sige-einweisungen`.
 *
 * Pfadschema wie bei uploadCustomPerformanceProof (invoiceService.ts): eine
 * UUID statt eines festen Dateinamens. Ein erneuter Upload ersetzt damit den
 * VERWEIS (`dokument_pfad`), ueberschreibt aber nie eine Datei, auf die ein
 * anderer Stand noch zeigt — `upsert: false` sorgt dafuer, dass ein zweiter
 * Schreibversuch auf denselben Pfad scheitert statt still zu ueberschreiben.
 *
 * Diese Funktion schreibt NICHT an der Einweisung. Der Aufrufer setzt
 * `dokument_pfad`/`dokument_name` anschliessend ueber updateEinweisung — und
 * laeuft damit durch den Entwurfs-Guard, der auch fuer alles andere gilt.
 */
export const uploadEinweisungsDokument = async (
  einweisungId: string,
  file: Blob,
  fileName: string,
): Promise<{ path: string; fileName: string }> => {
  const name = (fileName || '').trim() || 'einweisung.pdf';
  const path = `papier/${einweisungId}/${crypto.randomUUID()}.${endungAus(name)}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: false, contentType: file.type || undefined });
  if (error) throw new Error(describeError(error));
  return { path, fileName: name };
};

/**
 * Signierte URL zum Ansehen/Herunterladen des Papier-Dokuments.
 *
 * Der Bucket ist privat — es gibt keine oeffentliche URL, und das ist Absicht:
 * auf dem Blatt stehen Namen und Unterschriften.
 */
export const signedUrlFuerDokument = async (path: string): Promise<string> => {
  const p = (path || '').trim();
  if (!p) throw new Error('Zu dieser Einweisung ist kein Dokument hinterlegt.');
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(p, SIGNED_URL_SEKUNDEN);
  if (error) throw new Error(describeError(error));
  if (!data?.signedUrl) {
    throw new Error(
      'Das hinterlegte Dokument konnte nicht geladen werden — bitte erneut hochladen.',
    );
  }
  return data.signedUrl;
};

// ─── Abrechnung ─────────────────────────────────────────────────────────────

/**
 * Vermerkt die Buchung auf einer LV-Position — GENAU EINMAL je Einweisung.
 *
 * Rueckgabe `true` = dieser Aufruf hat gebucht. `false` = die Einweisung war
 * bereits gebucht, es wurde NICHTS geschrieben. Kein Fehler: der zweite
 * Aufruf ist der Normalfall (erneuter Export, wiederholter Versand), und ein
 * Fehler dafuer waere eine Meldung ueber ein Nicht-Ereignis.
 *
 * Der Schutz ist `.is('gebucht_am', null)` in der WHERE-Klausel — nicht ein
 * vorgelagertes SELECT (Falle (b) im Kopf dieser Datei). Der STATUS wird hier
 * bewusst NICHT geprueft: gebucht wird nach dem Abschliessen, und dann steht
 * die Einweisung auf `abgeschlossen`.
 */
export const markiereGebucht = async (
  einweisungId: string,
  positionId: string,
  menge: number,
): Promise<boolean> => {
  const { data, error } = await supabase
    .from(TABELLE)
    .update({
      gebucht_am: new Date().toISOString(),
      gebuchte_position_id: positionId,
      gebuchte_menge: menge,
      updated_at: new Date().toISOString(),
    })
    .eq('id', einweisungId)
    .is('gebucht_am', null)
    .select('id');
  if (error) throw new Error(describeError(error));
  return !!data && data.length > 0;
};

// ─── Online-Einweisung per Link/QR ──────────────────────────────────────────
//
// docs/sige-einweisung-online-plan.md: Online-Teilnahmen liegen in der
// EIGENEN Tabelle `sige_einweisung_teilnahmen` (Nr. 97), der Link wird NUR
// ueber die RPC `sige_einweisung_link_setzen` (Nr. 98) gesetzt.

const TEILNAHMEN_TABELLE = 'sige_einweisung_teilnahmen';

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));

const mapTeilnahme = (row: Record<string, unknown>): EinweisungOnlineTeilnahme => ({
  id: str(row.id),
  einweisung_id: str(row.einweisung_id),
  project_id: str(row.project_id),
  vorname: str(row.vorname),
  nachname: str(row.nachname),
  funktion: strOrNull(row.funktion),
  firma: str(row.firma),
  unterschrift: str(row.unterschrift),
  unterschrieben_am: str(row.unterschrieben_am),
  user_agent: strOrNull(row.user_agent),
  created_at: strOrNull(row.created_at) ?? undefined,
});

/**
 * Alle Online-Teilnahmen eines Projekts, aelteste zuerst — EINE Abfrage je
 * Projekt, die Ablage verteilt sie mit gruppiereOnlineJeEinweisung.
 *
 * Fail-open wie fetchEinweisungen: solange Migration Nr. 97 nicht gefahren
 * ist, gibt es eben keine Online-Teilnahmen. Nur SELECT — die Tabelle laesst
 * fuer `authenticated` nichts anderes zu (Betreiber 12.09.: kein Loeschen).
 */
export const fetchOnlineTeilnahmen = async (projectId: string): Promise<EinweisungOnlineTeilnahme[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  try {
    const { data, error } = await supabase
      .from(TEILNAHMEN_TABELLE)
      .select('*')
      .eq('project_id', id)
      .order('unterschrieben_am');
    if (error) throw error;
    return (data ?? []).map((r) => mapTeilnahme(r as Record<string, unknown>));
  } catch (e) {
    if (istFehlendeTabelle(e)) {
      console.warn('sige_einweisung_teilnahmen fehlt — Migration Nr. 97 noch nicht gefahren.');
      return [];
    }
    throw new Error(describeError(e));
  }
};

/**
 * Schaltet den Link an oder aus (RPC `sige_einweisung_link_setzen`).
 *
 * Bewusst KEIN Client-Update auf die Zeile (anders als sifa `setzeLink`): die
 * Regel „nur ein abgeschlossenes Blatt bekommt einen Link" gehoert in die
 * Datenbank, nicht nur in den Knopf. Aus = `link_token` wird NULL; ein
 * erneutes Einschalten erzeugt einen NEUEN Token, damit ein abfotografierter
 * Aushang nach dem Deaktivieren tot ist.
 *
 * Postgres-Fehler kommen im Klartext an (P0001 „erst abschließen", 42501 kein
 * Projektzugriff, PGRST202 Migration Nr. 98 offen) — describeError reicht sie
 * durch.
 */
export const setzeEinweisungLink = async (
  id: string,
  aktiv: boolean,
  aktivBis?: string | null,
): Promise<{ linkToken: string | null; linkAktivBis: string | null }> => {
  const einweisungId = (id || '').trim();
  if (!einweisungId) throw new Error('Ohne Einweisung lässt sich kein Link setzen.');
  const { data, error } = await supabase.rpc('sige_einweisung_link_setzen', {
    p_id: einweisungId,
    p_aktiv: !!aktiv,
    p_aktiv_bis: (aktivBis || '').trim() || null,
  });
  if (error) throw new Error(describeError(error));

  // jsonb kommt als Objekt, in seltenen Faellen als Zeichenkette.
  let row: Record<string, unknown> | null = null;
  if (typeof data === 'string') {
    try {
      row = JSON.parse(data) as Record<string, unknown>;
    } catch {
      row = null;
    }
  } else if (data && typeof data === 'object') {
    row = data as Record<string, unknown>;
  }
  return {
    linkToken: strOrNull(row?.linkToken ?? row?.link_token) || null,
    linkAktivBis: strOrNull(row?.linkAktivBis ?? row?.link_aktiv_bis) || null,
  };
};

/**
 * Die EINE Quelle fuer den QR-Code auf Aushang, SiGe-Plan und Handbuch (Plan
 * §5a): die Einweisung des Projekts mit aktivem Link und hoechster Nummer.
 *
 * `null`, wenn kein Link aktiv ist — dann wird NICHTS gedruckt, kein
 * Platzhalter. Und `null` bei JEDEM Fehler: der Plan-Export darf nie an einem
 * QR-Code scheitern, der nur eine Beigabe ist.
 */
export const ladeEinweisungQrFuerProjekt = async (
  projectId: string,
  origin: string,
  pathname: string,
): Promise<EinweisungQrQuelle | null> => {
  try {
    const liste = await fetchEinweisungen(projectId);
    const jetzt = new Date();
    const aktive = liste
      .filter((e) => e.status === 'abgeschlossen' && linkIstAktiv(e, jetzt) && !!e.link_token)
      .sort((a, b) => (b.nummer || 0) - (a.nummer || 0));
    const e = aktive[0];
    if (!e?.link_token) return null;
    const url = einweisungLinkUrl(origin, pathname, e.link_token);
    const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 1 });
    return { einweisungId: e.id, nummer: e.nummer, url, dataUrl };
  } catch (e) {
    console.warn('Einweisungs-QR konnte nicht geladen werden — der Export läuft ohne.', e);
    return null;
  }
};
